import { useState } from 'react';
import { makeStyles, tokens, Button, Spinner } from '@fluentui/react-components';
import {
    CheckmarkCircle24Filled,
    Search24Filled,
    List24Regular,
    Play24Regular,
    LockClosed20Regular,
    ArrowRight24Regular,
    Open16Regular,
} from '@fluentui/react-icons';
import type { SearchIndexerContextValue } from '../../hooks/useSearchIndexer';
import { RegisterSourceModal } from './RegisterSourceModal';
import type { SourceRegistrationRequest } from '@shared/dto/searchIndexer';

const TEAL = '#2ba6bb';

const useStyles = makeStyles({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    header: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden',
    },
    headerBanner: {
        padding: '28px 32px 24px',
        borderBottom: `3px solid ${TEAL}`,
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    },
    headerTitle: {
        fontSize: tokens.fontSizeBase600,
        fontWeight: tokens.fontWeightSemibold,
        color: '#0f172a',
        margin: '0 0 4px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    headerSubtitle: {
        fontSize: tokens.fontSizeBase300,
        color: '#475569',
        margin: 0,
    },
    progressBar: {
        display: 'flex',
        alignItems: 'center',
        padding: '16px 32px',
        gap: '0',
    },
    stepDot: {
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '13px',
        fontWeight: tokens.fontWeightBold,
        flexShrink: 0,
        zIndex: 1,
    },
    stepLabel: {
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        marginTop: '4px',
    },
    connector: {
        flex: 1,
        height: '2px',
        margin: '0 -1px',
        marginTop: '-8px',
    },
    stepsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0',
    },
    stepRow: {
        display: 'flex',
        padding: '0 32px',
        gap: '20px',
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    stepIcon: {
        width: '44px',
        height: '44px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: '20px',
    },
    stepContent: {
        flex: 1,
        padding: '20px 0',
    },
    stepNum: {
        fontSize: tokens.fontSizeBase100,
        fontWeight: tokens.fontWeightSemibold,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: '2px',
    },
    stepTitle: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        margin: '0 0 6px',
        color: tokens.colorNeutralForeground1,
    },
    stepDesc: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground3,
        margin: '0 0 16px',
        lineHeight: '1.5',
    },
    bulletList: {
        margin: '0 0 16px',
        paddingLeft: '0',
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    bullet: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: tokens.fontSizeBase200,
        color: '#475569',
    },
    statusLine: {
        fontSize: tokens.fontSizeBase200,
        color: TEAL,
        fontStyle: 'italic',
        marginBottom: '8px',
    },
    lockedRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        color: tokens.colorNeutralForeground4,
        fontSize: tokens.fontSizeBase200,
    },
    skipLink: {
        textAlign: 'center',
        padding: '16px',
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    },
});

interface StepConfig {
    num: number;
    title: string;
    desc: string;
    status: 'complete' | 'active' | 'locked';
}

interface Props {
    ctx: SearchIndexerContextValue;
    onSkipToFullView: () => void;
}

export function RegistrySetupWizard({ ctx, onSkipToFullView }: Props) {
    const styles = useStyles();
    const {
        registryExists,
        registryUrl,
        sources,
        isProvisioning,
        provisionStatus,
        provisionRegistry,
        registerSource,
        getSiteLists,
        getSourceColumns,
        error,
    } = ctx;

    const [registerModalOpen, setRegisterModalOpen] = useState(false);

    const step1Complete = registryExists === true;
    const step2Complete = sources.length > 0;
    const step3Complete = sources.some(s => s.lastFullSyncDate);

    const steps: StepConfig[] = [
        {
            num: 1,
            title: 'Create Search Registry',
            desc: 'Creates the InjioSearchRegistry list on your SharePoint site. This is a one-time setup that tracks all your indexed content sources.',
            status: step1Complete ? 'complete' : 'active',
        },
        {
            num: 2,
            title: 'Configure Site Pages',
            desc: 'Site Pages is added automatically. It\'s required for indexing and for your smart web part search to work. Just choose a Tag Field to categorize your pages before running your first index. You can register more sources later.',
            status: step1Complete ? (step2Complete ? 'complete' : 'active') : 'locked',
        },
        {
            num: 3,
            title: 'Run Your First Index',
            desc: 'The indexer reads your content, chunks it, and generates semantic embeddings. This may take a few minutes depending on the number of items.',
            status: step2Complete ? (step3Complete ? 'complete' : 'active') : 'locked',
        },
    ];

    const activeStep = step3Complete ? null : steps.find(s => s.status === 'active')?.num ?? null;

    const handleRegister = async (config: SourceRegistrationRequest) => {
        await registerSource(config);
        setRegisterModalOpen(false);
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                {/* Banner */}
                <div className={styles.headerBanner}>
                    <h2 className={styles.headerTitle}>
                        <span
                            style={{
                                width: 40, height: 40, borderRadius: 10,
                                background: TEAL, display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                color: '#fff', flexShrink: 0,
                            }}
                        >
                            <Search24Filled />
                        </span>
                        Set Up Search Indexer
                    </h2>
                    <p className={styles.headerSubtitle}>
                        Complete 3 quick steps to enable semantic search on your SharePoint site.
                    </p>
                </div>

                {/* Step progress dots */}
                <div className={styles.progressBar}>
                    {steps.map((step, i) => (
                        <>
                            <div
                                key={step.num}
                                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                            >
                                <div
                                    className={styles.stepDot}
                                    style={{
                                        backgroundColor:
                                            step.status === 'complete' ? '#16a34a' :
                                            step.status === 'active' ? TEAL :
                                            '#e2e8f0',
                                        color:
                                            step.status === 'locked' ? '#94a3b8' : '#fff',
                                    }}
                                >
                                    {step.status === 'complete' ? '✓' : step.num}
                                </div>
                                <span
                                    className={styles.stepLabel}
                                    style={{
                                        color:
                                            step.status === 'complete' ? '#16a34a' :
                                            step.status === 'active' ? TEAL :
                                            '#94a3b8',
                                    }}
                                >
                                    {step.title}
                                </span>
                            </div>
                            {i < steps.length - 1 && (
                                <div
                                    key={`c${i}`}
                                    className={styles.connector}
                                    style={{
                                        backgroundColor:
                                            steps[i].status === 'complete' ? '#16a34a' : '#e2e8f0',
                                    }}
                                />
                            )}
                        </>
                    ))}
                </div>

                {/* Step rows */}
                <div className={styles.stepsWrapper}>
                    {steps.map(step => (
                        <div key={step.num} className={styles.stepRow}>
                            {/* Icon column */}
                            <div
                                className={styles.stepIcon}
                                style={{
                                    backgroundColor:
                                        step.status === 'complete' ? '#dcfce7' :
                                        step.status === 'active' ? '#e0f2fe' :
                                        '#f1f5f9',
                                }}
                            >
                                {step.status === 'complete' ? (
                                    <CheckmarkCircle24Filled style={{ color: '#16a34a' }} />
                                ) : step.num === 1 ? (
                                    <List24Regular style={{ color: step.status === 'active' ? TEAL : '#cbd5e1' }} />
                                ) : step.num === 2 ? (
                                    <Search24Filled style={{ color: step.status === 'active' ? TEAL : '#cbd5e1' }} />
                                ) : (
                                    <Play24Regular style={{ color: step.status === 'active' ? TEAL : '#cbd5e1' }} />
                                )}
                            </div>

                            {/* Content column */}
                            <div className={styles.stepContent}>
                                <div
                                    className={styles.stepNum}
                                    style={{
                                        color:
                                            step.status === 'complete' ? '#16a34a' :
                                            step.status === 'active' ? TEAL :
                                            '#94a3b8',
                                    }}
                                >
                                    Step {step.num} {step.status === 'complete' ? ': Complete' : step.status === 'locked' ? ': Locked' : ': Action Required'}
                                </div>
                                <h3
                                    className={styles.stepTitle}
                                    style={{ color: step.status === 'locked' ? '#94a3b8' : undefined }}
                                >
                                    {step.title}
                                </h3>

                                {step.status !== 'locked' && (
                                    <p className={styles.stepDesc}>{step.desc}</p>
                                )}

                                {/* Step 1 actions */}
                                {step.num === 1 && step.status === 'active' && (
                                    <>
                                        <ul className={styles.bulletList}>
                                            {[
                                                'Creates InjioSearchRegistry list on your SharePoint site',
                                                'Adds 14 schema columns for source tracking and concurrency control',
                                                'Completely safe. Does not modify any existing data',
                                            ].map(b => (
                                                <li key={b} className={styles.bullet}>
                                                    <span style={{ color: TEAL, fontWeight: 700 }}>•</span> {b}
                                                </li>
                                            ))}
                                        </ul>
                                        {isProvisioning && provisionStatus && (
                                            <p className={styles.statusLine}>{provisionStatus}</p>
                                        )}
                                        {error && (
                                            <p style={{ color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200, marginBottom: '8px' }}>
                                                {error}
                                            </p>
                                        )}
                                        <Button
                                            appearance="primary"
                                            icon={isProvisioning ? <Spinner size="tiny" /> : <List24Regular />}
                                            onClick={provisionRegistry}
                                            disabled={isProvisioning}
                                            style={{ backgroundColor: TEAL, borderColor: TEAL }}
                                        >
                                            {isProvisioning ? provisionStatus || 'Setting up…' : 'Create Registry'}
                                        </Button>
                                    </>
                                )}

                                {step.num === 1 && step.status === 'complete' && (
                                    <p style={{ fontSize: tokens.fontSizeBase200, color: '#16a34a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        InjioSearchRegistry is ready on your site.
                                        {registryUrl && (
                                            <a
                                                href={registryUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: TEAL, textDecoration: 'none' }}
                                            >
                                                Open in SharePoint
                                                <Open16Regular style={{ width: 12, height: 12 }} />
                                            </a>
                                        )}
                                    </p>
                                )}

                                {/* Step 2 actions */}
                                {step.num === 2 && step.status === 'active' && (
                                    <>
                                        <ul className={styles.bulletList}>
                                            {[
                                                'Site Pages is selected automatically, required for indexing and smart search',
                                                'Choose a Tag Field to categorize your pages',
                                                'A dedicated embedding cache list is created automatically',
                                            ].map(b => (
                                                <li key={b} className={styles.bullet}>
                                                    <span style={{ color: TEAL, fontWeight: 700 }}>•</span> {b}
                                                </li>
                                            ))}
                                        </ul>
                                        <Button
                                            appearance="primary"
                                            icon={<Search24Filled />}
                                            onClick={() => setRegisterModalOpen(true)}
                                            style={{ backgroundColor: TEAL, borderColor: TEAL }}
                                        >
                                            Set Up Site Pages
                                        </Button>
                                    </>
                                )}

                                {step.num === 2 && step.status === 'complete' && (
                                    <p style={{ fontSize: tokens.fontSizeBase200, color: '#16a34a' }}>
                                        {sources.length} source{sources.length > 1 ? 's' : ''} registered.
                                    </p>
                                )}

                                {/* Step 3 actions */}
                                {step.num === 3 && step.status === 'active' && (
                                    <>
                                        <p className={styles.stepDesc}>{step.desc}</p>
                                        <Button
                                            appearance="primary"
                                            icon={<Play24Regular />}
                                            onClick={onSkipToFullView}
                                            style={{ backgroundColor: TEAL, borderColor: TEAL }}
                                        >
                                            Go to Index Run
                                            <ArrowRight24Regular style={{ marginLeft: 4 }} />
                                        </Button>
                                    </>
                                )}

                                {step.num === 3 && step.status === 'complete' && (
                                    <p style={{ fontSize: tokens.fontSizeBase200, color: '#16a34a' }}>
                                        Index is up to date.
                                    </p>
                                )}

                                {/* Locked state */}
                                {step.status === 'locked' && (
                                    <div className={styles.lockedRow}>
                                        <LockClosed20Regular />
                                        <span>Complete step {step.num - 1} first</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Skip link */}
                {activeStep !== 3 && (
                    <div className={styles.skipLink}>
                        <button
                            type="button"
                            onClick={onSkipToFullView}
                            style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: '#64748b', fontSize: '13px',
                                textDecoration: 'underline', fontFamily: 'inherit',
                            }}
                        >
                            Skip guided setup and go to full admin view
                        </button>
                    </div>
                )}
            </div>

            <RegisterSourceModal
                open={registerModalOpen}
                onClose={() => setRegisterModalOpen(false)}
                onRegister={handleRegister}
                getSiteLists={getSiteLists}
                getSourceColumns={getSourceColumns}
                lockToSitePages
            />
        </div>
    );
}
